import { useMemo } from 'react';
import type { DraftState, TeamLiveData } from '../types';
import type { TeamValuation } from '../lib/valuation';
import { TEAMS_BY_ABBR, teamFullName } from '../constants/teams';
import { BASELINE_PROJECTIONS } from '../constants/teamProjections';
import { TeamLogo } from './TeamLogo';

interface Props {
  state: DraftState;
  valuations: Record<string, TeamValuation>;
  pastPrices: Record<string, number>;
  liveData: Record<string, TeamLiveData>;
  canNominate: boolean;
  onNominate: (abbr: string) => void;
  limit?: number;
}

interface BargainRow {
  abbr: string;
  suggested: number;
  lastPrice: number | null;
  proj: number | null;
  /** absolute $ delta — positive means our suggested $ exceeds last yr (i.e. it's a bargain) */
  delta: number;
  /** suggested$ / lastYear$ ratio (used when lastPrice is missing we fall back to leagueAvgPrior) */
  ratio: number;
  reason: 'priced-low' | 'on-the-rise' | 'no-history';
}

/**
 * Surfaces the biggest pricing inefficiencies on the board:
 *   "Priced low" — last yr price was well below this year's suggested value
 *                  (team got better, or the league underbid it)
 *   "On the rise" — no prior price but projected wins ranks well
 */
export function TopBargains({
  state, valuations, pastPrices, liveData, canNominate, onNominate, limit = 5,
}: Props) {
  const rows: BargainRow[] = useMemo(() => {
    const out: BargainRow[] = [];
    for (const abbr of state.availableTeams) {
      const v = valuations[abbr];
      if (!v) continue;
      const lastPrice = pastPrices[abbr] ?? null;
      const proj = liveData[abbr]?.projectedWins ?? BASELINE_PROJECTIONS[abbr]?.projectedWins ?? null;
      if (lastPrice != null && lastPrice > 0) {
        const delta = v.suggested - lastPrice;
        const ratio = v.suggested / Math.max(1, lastPrice);
        out.push({
          abbr, suggested: v.suggested, lastPrice, proj, delta, ratio,
          reason: 'priced-low',
        });
      } else {
        // No history: flag if FPI projection is strong relative to suggested
        const ratio = v.suggested / Math.max(1, v.leagueAvgPrior);
        out.push({
          abbr, suggested: v.suggested, lastPrice: null, proj, delta: 0, ratio,
          reason: proj != null && proj >= 9 ? 'on-the-rise' : 'no-history',
        });
      }
    }
    // Sort: positive delta first, then by ratio, exclude obvious overpays
    out.sort((a, b) => {
      if (a.reason === 'priced-low' && b.reason === 'priced-low') {
        return b.delta - a.delta;
      }
      if (a.reason === 'priced-low') return -1;
      if (b.reason === 'priced-low') return 1;
      return b.ratio - a.ratio;
    });
    return out.filter(r => r.reason !== 'no-history' || r.proj != null);
  }, [state.availableTeams, valuations, pastPrices, liveData]);

  const top = rows.slice(0, limit);
  if (top.length === 0) return null;

  return (
    <div className="panel p-4">
      <div className="flex items-end justify-between mb-3">
        <div>
          <h2 className="text-lg font-extrabold text-white">💎 Top Bargains on the Board</h2>
          <p className="text-[10px] text-slate-500">Where this year's projected value exceeds last year's price.</p>
        </div>
        <span className="text-[10px] text-slate-500">Top {top.length}</span>
      </div>

      <ul className="space-y-1.5">
        {top.map((r, i) => {
          const meta = TEAMS_BY_ABBR[r.abbr];
          const positive = r.delta > 0;
          return (
            <li
              key={r.abbr}
              className="flex items-center gap-3 p-2 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-emerald-400 transition"
              style={{ borderLeft: `4px solid ${meta?.color ?? '#475569'}` }}
            >
              <span className="text-xs font-mono w-5 text-slate-500">#{i + 1}</span>
              <TeamLogo abbr={r.abbr} size={28} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-white truncate">{teamFullName(r.abbr)}</div>
                <div className="text-[10px] text-slate-400 flex flex-wrap gap-2">
                  <span className="text-emerald-300">≈${r.suggested}</span>
                  {r.lastPrice != null ? (
                    <>
                      <span className="text-amber-300">last ${r.lastPrice}</span>
                      <span className={positive ? 'text-emerald-400 font-bold' : 'text-rose-400 font-bold'}>
                        {positive ? '+' : ''}${r.delta}
                      </span>
                    </>
                  ) : (
                    <span className="text-slate-500 italic">no prior price</span>
                  )}
                  {r.proj != null && (
                    <span className="text-slate-400">{r.proj.toFixed(1)} proj W</span>
                  )}
                </div>
              </div>
              {canNominate && (
                <button
                  onClick={() => onNominate(r.abbr)}
                  className="text-[10px] px-2 py-1 bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded font-bold whitespace-nowrap"
                >Nominate</button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
